import type { Hero, HeroSkill } from "@/types/hero.types"
import type { HeroInventory } from "@/types/item.types"
import { getEmptyShrineBuffs } from "@/systems/shrine-system"

const CLONE_STORAGE_KEY = "pelf_hero_clones"
const CLONE_SECRET = "pelf_clone_integrity_v1"

export interface ClonedHero {
  id: string
  hero: SerializedHero
  inventory: HeroInventory
  clonedAt: number
  checksum: string
}

// Serialized hero without runtime state
export interface SerializedHero {
  type: Hero["type"]
  name: string
  maxHealth: number
  maxMana: number
  damage: Hero["damage"]
  armor: number
  damageType: Hero["damageType"]
  armorType: Hero["armorType"]
  speed: number
  attackRange: number
  attackSpeed: number
  color: string
  upgrades: Hero["upgrades"]
  abilities: Hero["abilities"]
  skills: SerializedSkill[]
  level: number
  experience: number
  experienceToNextLevel: number
  damageMultiplier: number
  healthMultiplier: number
  skillPoints: number
}

// Serialized skill without runtime state
interface SerializedSkill {
  id: string
  name: string
  description: string
  type: HeroSkill["type"]
  hotkey: string
  iconColor: string
  icon?: string
  maxLevel: number
  levels: HeroSkill["levels"]
  currentLevel: number
  isUnlocked: boolean
  isTargetable?: boolean
}

// Simple hash function using Web Crypto API
async function computeChecksum(data: string): Promise<string> {
  const encoder = new TextEncoder()
  const dataWithSecret = data + CLONE_SECRET
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(dataWithSecret))
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("")
}

// Serialize hero for storage (remove runtime state)
function serializeHero(hero: Hero): SerializedHero {
  return {
    type: hero.type,
    name: hero.name,
    maxHealth: hero.maxHealth,
    maxMana: hero.maxMana,
    damage: { ...hero.damage },
    armor: hero.armor,
    damageType: hero.damageType,
    armorType: hero.armorType,
    speed: hero.speed,
    attackRange: hero.attackRange,
    attackSpeed: hero.attackSpeed,
    color: hero.color,
    upgrades: { ...hero.upgrades },
    abilities: hero.abilities.map(a => ({ ...a })),
    skills: hero.skills.map(s => ({
      id: s.id,
      name: s.name,
      description: s.description,
      type: s.type,
      hotkey: s.hotkey,
      iconColor: s.iconColor,
      icon: s.icon,
      maxLevel: s.maxLevel,
      levels: s.levels.map(l => ({ ...l })),
      currentLevel: s.currentLevel,
      isUnlocked: s.isUnlocked,
      isTargetable: s.isTargetable,
    })),
    level: hero.level,
    experience: hero.experience,
    experienceToNextLevel: hero.experienceToNextLevel,
    damageMultiplier: hero.damageMultiplier,
    healthMultiplier: hero.healthMultiplier,
    skillPoints: hero.skillPoints,
  }
}

// Load all clones from storage
export function loadClones(): ClonedHero[] {
  if (typeof window === "undefined") return []

  try {
    const stored = localStorage.getItem(CLONE_STORAGE_KEY)
    if (!stored) return []
    return JSON.parse(stored) as ClonedHero[]
  } catch {
    return []
  }
}

// Save clones to storage
function saveClones(clones: ClonedHero[]): void {
  if (typeof window === "undefined") return
  localStorage.setItem(CLONE_STORAGE_KEY, JSON.stringify(clones))
}

// Clone a hero and save to storage
export async function cloneHero(
  hero: Hero,
  inventory: HeroInventory
): Promise<ClonedHero> {
  const serializedHero = serializeHero(hero)
  const serializedInventory: HeroInventory = {
    slots: inventory.slots.map(slot =>
      slot ? { ...slot, currentCooldown: 0 } : null
    ),
  }

  const cloneData = JSON.stringify({ hero: serializedHero, inventory: serializedInventory })
  const checksum = await computeChecksum(cloneData)

  const clone: ClonedHero = {
    id: `clone_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    hero: serializedHero,
    inventory: serializedInventory,
    clonedAt: Date.now(),
    checksum,
  }

  const clones = loadClones()
  clones.push(clone)
  saveClones(clones)

  return clone
}

// Verify a clone's integrity
export async function verifyClone(clone: ClonedHero): Promise<boolean> {
  const cloneData = JSON.stringify({ hero: clone.hero, inventory: clone.inventory })
  const expectedChecksum = await computeChecksum(cloneData)
  return expectedChecksum === clone.checksum
}

// Delete a clone
export function deleteClone(cloneId: string): void {
  const clones = loadClones()
  const filtered = clones.filter(c => c.id !== cloneId)
  saveClones(filtered)
}

// Clear all clones (used when starting a new game)
export function clearAllClones(): void {
  if (typeof window === "undefined") return
  localStorage.removeItem(CLONE_STORAGE_KEY)
}

// Get a verified clone by ID
export async function getVerifiedClone(cloneId: string): Promise<ClonedHero | null> {
  const clones = loadClones()
  const clone = clones.find(c => c.id === cloneId)
  if (!clone) return null

  const isValid = await verifyClone(clone)
  if (!isValid) {
    // Clone was tampered with, delete it
    deleteClone(cloneId)
    return null
  }

  return clone
}

// Get the most recent clone (synchronously, without verification)
// Used during game loop restoration where async is not practical
export function getLatestClone(): ClonedHero | null {
  const clones = loadClones()
  if (clones.length === 0) return null
  return clones[clones.length - 1]
}

// Pop (get and delete) the most recent clone
export function popLatestClone(): ClonedHero | null {
  const clones = loadClones()
  if (clones.length === 0) return null
  const latest = clones[clones.length - 1]
  deleteClone(latest.id)
  return latest
}

// Restore hero from clone
export function restoreHeroFromClone(
  clone: ClonedHero,
  position: { x: number; y: number }
): Hero {
  const serialized = clone.hero

  return {
    id: `hero_${Date.now()}`,
    type: serialized.type,
    name: serialized.name,
    position: { ...position },
    targetPosition: null,
    shrineBuffs: getEmptyShrineBuffs(),
    health: serialized.maxHealth,
    maxHealth: serialized.maxHealth,
    mana: serialized.maxMana,
    maxMana: serialized.maxMana,
    damage: { ...serialized.damage },
    armor: serialized.armor,
    damageType: serialized.damageType,
    armorType: serialized.armorType,
    speed: serialized.speed,
    attackRange: serialized.attackRange,
    attackSpeed: serialized.attackSpeed,
    attackCooldown: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: serialized.color,
    rotation: 0,
    upgrades: { ...serialized.upgrades },
    abilities: serialized.abilities.map(a => ({ ...a })),
    skills: serialized.skills.map(s => ({
      ...s,
      currentCooldown: 0,
    })),
    level: serialized.level,
    experience: serialized.experience,
    experienceToNextLevel: serialized.experienceToNextLevel,
    damageMultiplier: serialized.damageMultiplier,
    healthMultiplier: serialized.healthMultiplier,
    skillPoints: serialized.skillPoints,
    isDashing: false,
    dashDirection: null,
    dashProgress: 0,
    dashMaxDistance: 0,
    dashDamage: 0,
    dashSpeed: 0,
    dashSlowValue: 0,
    dashSlowDuration: 0,
    dashHitEnemies: [],
    activeBuffs: [],
    // Animation state
    animationState: "idle" as const,
    animationStartTime: Date.now(),
    hurtTimer: 0,
    lastMovementTime: Date.now(),
    isPlayingRelaxedIdle: false,
    activeSkillAnimation: null,
    randomAttackVariant: 0,
    lastIdleAnimationTime: Date.now(),
    selectedIdleVariant: 0,
    // Forcefield (Rift Blade only)
    forcefield: serialized.type === "paladin" ? Math.floor(serialized.maxHealth * 0.2) : 0,
    maxForcefield: serialized.type === "paladin" ? Math.floor(serialized.maxHealth * 0.2) : 0,
    // Slow debuff (reset on restore)
    slowMultiplier: 1,
    slowEndFrame: 0,
  }
}
